import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  IconButton,
  Box,
  Button,
  Container,
  Breadcrumbs,
  Link as MUILink,
} from "@mui/material";
import { Link } from "react-router-dom";
import moment from "moment";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { message, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageTitle from "../../Components/PageTitle";
import {
  getCourses,
  addCourse,
  deleteCourse,
} from "../../redux/actions/admin/courseAction";
import Table from "../common/Table";
import EditCourse from "./EditCourse";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Courses = () => {
  const dispatch = useDispatch();
  const { courses, loading, error } = useSelector((state) => state.courses);
  const [value, setValue] = useState(0);
  const [search, setSearch] = useState("");
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [name, setName] = useState("");
  const [fees, setFees] = useState("");
  const [desc, setDesc] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(startDate);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  useEffect(() => {
    dispatch(getCourses());
  }, [dispatch]);

  useEffect(() => {
    setFilteredCourses(courses);
  }, [courses]);

  // Function to refresh the Events & News data
  const refreshData = () => {
    dispatch(getCourses()); // Refresh data after update
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Filter teachers based on search query
  const filteredNewCourse = filteredCourses?.filter((Course) =>
    Course?.name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  // Handle Delete Api Course
  const { confirm } = Modal;
  const handleDeleteCourse = async (id) => {
    confirm({
      title: "Are you sure you want to delete this Course?",
      content: "Once deleted, this course cannot be recovered.",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        await dispatch(deleteCourse(id));
        await dispatch(getCourses());
      },
    });
  };

  const courseStartDate = moment(startDate).format("MM/DD/YYYY");
  const courseEndDate = moment(endDate).format("MM/DD/YYYY");

  // Handle Api Course
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name) {
      message.error("Please enter a course name");
      return;
    }
    if (!fees) {
      message.error("Please enter a course fees");
      return;
    }
    //  if (!desc) {
    //   message.error("Please enter a short course description");
    //   return;
    // }
    if (!startDate) {
      message.error("Please select a course start date");
      return;
    }
    if (!endDate) {
      message.error("Please select a course end date");
      return;
    }
    const courseData = {
      name,
      fees,
      desc,
      startDate: courseStartDate,
      endDate: courseEndDate,
    };
    await dispatch(addCourse(courseData));
    await dispatch(getCourses());
    setName("");
    setFees("");
    setDesc("");
    setStartDate(null);
    setEndDate(null);
  };

  const columns = [
    {
      field: "rowNumber",
      headerName: "#",
      width: 10,
      sortable: false,
    },
    {
      field: "Actions",
      width: 120,
      renderCell: (value) => (
        <Box>
          <IconButton
            aria-label="delete"
            onClick={() => handleDeleteCourse(value?.row?._id)}
          >
            <DeleteIcon sx={{ color: "red" }} />
          </IconButton>
          <EditCourse
            id={value?.row?._id}
            name={value?.row?.name}
            fees={value?.row?.fees}
            desc={value?.row?.desc}
            startDate={value?.row?.startDate}
            endDate={value?.row?.endDate}
            refreshData={refreshData}
          />
        </Box>
      ),
    },

    {
      field: "name",
      headerName: "Course Name",
      width: 180,
      editable: true,
    },
    {
      field: "fees",
      headerName: "Course Fees",
      width: 180,
      editable: true,
    },

    {
      field: "desc",
      headerName: "Course Description",
      width: 350,
      renderCell: (params) => (
        <div
          style={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 1,
          }}
          dangerouslySetInnerHTML={{ __html: params.row.desc }}
        />
      ),
    },

    {
      field: "startDate",
      headerName: "Course Start Date",
      width: 170,
      editable: true,
    },

    {
      field: "endDate",
      headerName: "Course end Date",
      width: 170,
      editable: true,
    },

    {
      field: "createdAt",
      headerName: "CreatedAt",
      width: 150,
      renderCell: (params) =>
        moment(params?.row?.createdAt).format("MM/DD/YYYY"),
    },
  ];

  return (
    <>
      <PageTitle
        title="Manage Courses - Admin Panel | Shree Mangalam School"
        description="Create, update, and manage academic courses in the Admin Panel of Shree Mangalam School. Structure curriculum offerings for RIMC, Sainik School, and more."
        url="/admin/courses"
        keywords="admin courses, manage school courses, Shree Manglam course panel, RIMC course admin, Sainik School course management, academic program admin"
      />

      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Courses
      </Typography>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>Courses</Typography>
      </Breadcrumbs>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Courses List" {...TabPanel(0)} />
          <Tab label="Add Courses" {...TabPanel(1)} />
        </Tabs>
      </Box>
      {/* Events & News List */}
      <CustomTabPanel value={value} index={0}>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Courses List
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Courses Name"
          value={search}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <IconButton>
                <SearchIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            ),
          }}
          sx={{
            mb: 3,
            mt: 2,
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
        <Table data={filteredNewCourse} columns={columns} />
      </CustomTabPanel>

      {/* Create a New Add Events & News */}
      <CustomTabPanel value={value} index={1} maxWidth>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Create a new Courses
        </Typography>
        <Container
          onSubmit={handleSubmit}
          component="form"
          className="form_style border-style"
          maxWidth
        >
          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="CoursesName"
            label="Courses Name *"
            name="CoursesName"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Courses Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              "& input[type='number']": {
                MozAppearance: "textfield",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  display: "none",
                },
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="courseFees"
            label="Course Fees *"
            name="courseFees"
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Course Fees"
            value={fees}
            onChange={(e) => setFees(e.target.value)}
          />
          <h4>Courses Description </h4>
          <ReactQuill
            value={desc}
            onChange={setDesc}
            placeholder="Courses Description"
            style={{
              // height: "200px",
              marginBottom: "30px",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "10px",
            }}
          />

          <Box className="Date">
            <Box sx={{ mt: 3 }}>
              <h4>Course Start Date</h4>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                isClearable
                placeholderText="Course Start Date"
                selected={startDate}
                onChange={handleStartDateChange}
                minDate={new Date()}
                maxDate={endDate}
                value={startDate}
              />
            </Box>
            <Box sx={{ mt: 3, mb: 3 }}>
              <h4>Course End Date</h4>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                isClearable
                placeholderText="Course End Date"
                selected={endDate}
                onChange={handleEndDateChange}
                minDate={(new Date() && startDate) || new Date()}
                value={endDate}
                maxDate={endDate}
              />
            </Box>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="courses_desc"
            sx={{
              mt: 3,
              borderRadius: "5px",
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              letterSpacing: ".1rem",
            }}
          >
            Add Courses
          </Button>
        </Container>
      </CustomTabPanel>
    </>
  );
};

export default Courses;
